/**
 * Per-flow merge logic for the client-side AI resolver.
 *
 * Two entry points:
 *  - `mergeUserChangeWithAi` — when the user shipped an op on a flow
 *    that ALSO has a pending AI block; combine the two.
 *  - `synthesizeAiOnlyFlow` — when a flow has a pending AI block but
 *    the user didn't touch it; produce the resolution op from scratch.
 *
 * Both are pure; the resolver in `./index.ts` decides which to call.
 */

import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { PreOrderFlowChange } from "@stockline/web";
import type { AiResolveContext, AiDecision } from "./index";
import {
  type AiSuggestionBlock,
  PRE_ORDER_FLOW_STATUS_PENDING,
  FLOW_METADATA_BLOB_FIELD,
  assembleResolvedMetadata,
  extractMetadataBlob,
  flowCamelKeyToSnake,
  jsonEqual,
  resolveBlocks,
} from "./helpers";

type FlowData = Record<string, unknown>;

/**
 * Merge a user's op on a flow with the flow's pending AI block. The
 * result is one or more `PreOrderFlowChange`s the push-batch loop
 * will apply.
 */
export function mergeUserChangeWithAi(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  change: PreOrderFlowChange,
  decision: AiDecision,
): PreOrderFlowChange[] {
  // User wants to delete: emit a single delete op. Block stays pending
  // on a tombstoned row; queries filter deleted rows so it's invisible.
  if (change.op === "delete") {
    return [{ op: "delete", id: flow.id }];
  }
  // Create on top of a row with pending AI is unusual — pass through.
  if (change.op === "create") {
    return [change];
  }

  const userData = change.data as FlowData;

  if (decision === "decline") {
    return mergeDeclineWithUserEdit(ctx, flow, blocks, pending, userData);
  }
  if (pending.type === "delete") {
    return mergeAcceptDeleteWithUserEdit(ctx, flow, blocks, pending, userData);
  }
  if (pending.type === "add") {
    return mergeAcceptAddWithUserEdit(ctx, flow, blocks, pending, userData);
  }
  // pending.type === "modify"
  return mergeAcceptModifyWithUserEdit(ctx, flow, blocks, pending, userData);
}

// ---------- accept/decline merge branches ----------

function mergeDeclineWithUserEdit(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userData: FlowData,
): PreOrderFlowChange[] {
  // Declining the AI proposal must not discard the user's own staged
  // edit — neither their column writes (already spread below) nor their
  // metadata blob (m3_comments, …). Pull the blob out as a layer so the
  // flipped blocks still land last.
  const columnWrites: Record<string, unknown> = { ...userData };
  const userMetaBlob = extractMetadataBlob(
    columnWrites,
    FLOW_METADATA_BLOB_FIELD,
  );
  const flipped = resolveBlocks(
    blocks,
    pending,
    "declined",
    ctx.userId,
    ctx.resolvedAt,
  );
  const flowMetadata = assembleResolvedMetadata(
    flow.flow_metadata,
    [userMetaBlob],
    flipped,
  );
  return [
    {
      op: "update",
      id: flow.id,
      data: { ...columnWrites, flowMetadata },
    },
  ];
}

/**
 * User editing a row AI proposed to delete → keep it alive, block
 * stamps `declined`, user's edit applied.
 */
function mergeAcceptDeleteWithUserEdit(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userData: FlowData,
): PreOrderFlowChange[] {
  return mergeDeclineWithUserEdit(ctx, flow, blocks, pending, userData);
}

/**
 * User accepting an AI-added line (with possible edits). Status flips
 * `5 → 0`. Block stamps `modified` only when a user field differs from
 * the row's current column value (which IS the AI's proposal for
 * type:add).
 *
 * The user may also have staged a `flowMetadata` blob edit (per-line
 * `m3_comments`, `nb_colis`, …) on the AI-added row. That blob is merged
 * into the resolved metadata BEFORE the flipped `ai_suggestions[]` are
 * written — without it, `flowMetadata` would be rebuilt from
 * `flow.flow_metadata` only and the spread below would clobber the
 * user's staged blob (dropping the m3 comment on accept).
 */
function mergeAcceptAddWithUserEdit(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userData: FlowData,
): PreOrderFlowChange[] {
  const overrides = collectAddOverrides(flow, userData);
  const finalStatus: "accepted" | "modified" =
    Object.keys(overrides).length > 0 ? "modified" : "accepted";
  const flipped = resolveBlocks(
    blocks,
    pending,
    finalStatus,
    ctx.userId,
    ctx.resolvedAt,
    finalStatus === "modified" ? overrides : undefined,
  );
  // Pull the user's staged metadata blob out of the column writes so it
  // doesn't land as a literal `flowMetadata` column value, and fold it
  // into the resolved metadata as a layer (otherwise it's clobbered).
  const columnWrites: Record<string, unknown> = { ...userData };
  const userMetaBlob = extractMetadataBlob(
    columnWrites,
    FLOW_METADATA_BLOB_FIELD,
  );
  const flowMetadata = assembleResolvedMetadata(
    flow.flow_metadata,
    [userMetaBlob],
    flipped,
  );
  return [
    {
      op: "update",
      id: flow.id,
      data: {
        ...columnWrites,
        status: PRE_ORDER_FLOW_STATUS_PENDING,
        flowMetadata,
      },
    },
  ];
}

/**
 * User accepting an AI modify proposal (with possible per-field
 * overrides). The merged write carries every AI-proposed field except
 * those the user wrote a materially different value for; those land
 * as `finalTo` on the block.
 */
function mergeAcceptModifyWithUserEdit(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userData: FlowData,
): PreOrderFlowChange[] {
  const blockChanges = pending.changes ?? {};
  const { overrides, aiOnly } = splitModifyIntoBuckets(blockChanges, userData);
  const blockStatus: "accepted" | "modified" =
    Object.keys(overrides).length > 0 ? "modified" : "accepted";
  // Flip the block BEFORE the blob extractions below mutate `overrides` —
  // `finalTo` must capture the user's override value (incl. a metadata
  // blob override) while it's still present.
  const flipped = resolveBlocks(
    blocks,
    pending,
    blockStatus,
    ctx.userId,
    ctx.resolvedAt,
    blockStatus === "modified" ? overrides : undefined,
  );
  const aiMetaBlob = extractMetadataBlob(aiOnly, FLOW_METADATA_BLOB_FIELD);
  const overrideMetaBlob = extractMetadataBlob(
    overrides,
    FLOW_METADATA_BLOB_FIELD,
  );
  const userMetaBlob = extractMetadataBlob(
    { ...userData },
    FLOW_METADATA_BLOB_FIELD,
  );
  // Precedence: live row < AI-applied < user-staged < user override.
  const flowMetadata = assembleResolvedMetadata(
    flow.flow_metadata,
    [aiMetaBlob, userMetaBlob, overrideMetaBlob],
    flipped,
  );
  const independentUserFields = pickIndependentFlowFields(
    userData,
    blockChanges,
  );
  return [
    {
      op: "update",
      id: flow.id,
      data: {
        ...aiOnly,
        ...overrides,
        ...independentUserFields,
        flowMetadata,
      } as PreOrderFlowChange & { op: "update" } extends { data: infer D }
        ? D
        : never,
    },
  ];
}

// ---------- AI-only flow synthesis ----------

/**
 * Produce the per-flow resolution op for a flow that has a pending AI
 * block but the user didn't touch it. Accept lands the AI's values;
 * decline flips the block (and tombstones type:add rows since the
 * AI-proposed line is gone).
 */
export function synthesizeAiOnlyFlow(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  decision: AiDecision,
): PreOrderFlowChange[] {
  if (pending.type === "delete") {
    return synthesizeDelete(ctx, flow, blocks, pending, decision);
  }
  if (pending.type === "add") {
    return synthesizeAdd(ctx, flow, blocks, pending, decision);
  }
  return synthesizeModify(ctx, flow, blocks, pending, decision);
}

function synthesizeDelete(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  decision: AiDecision,
): PreOrderFlowChange[] {
  if (decision === "accept") {
    // Soft-delete; block stays pending on the tombstoned row.
    return [{ op: "delete", id: flow.id }];
  }
  // Decline: row stays, block flipped to declined.
  return [flipFlowBlockOnly(ctx, flow, blocks, pending, "declined")];
}

function synthesizeAdd(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  decision: AiDecision,
): PreOrderFlowChange[] {
  if (decision === "decline") {
    // Flip the block to `declined` BEFORE the soft-delete so the
    // tombstoned row's `flow_metadata.ai_suggestions[]` records the
    // resolution — otherwise the audit trail leaves the block at
    // `pending` even though the AI-added line was rejected.
    return [
      flipFlowBlockOnly(ctx, flow, blocks, pending, "declined"),
      { op: "delete", id: flow.id },
    ];
  }
  const flipped = resolveBlocks(
    blocks,
    pending,
    "accepted",
    ctx.userId,
    ctx.resolvedAt,
  );
  const flowMetadata = assembleResolvedMetadata(
    flow.flow_metadata,
    [],
    flipped,
  );
  return [
    {
      op: "update",
      id: flow.id,
      data: {
        status: PRE_ORDER_FLOW_STATUS_PENDING,
        flowMetadata,
      },
    },
  ];
}

function synthesizeModify(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  decision: AiDecision,
): PreOrderFlowChange[] {
  if (decision === "decline") {
    return [flipFlowBlockOnly(ctx, flow, blocks, pending, "declined")];
  }
  const aiFields: Record<string, unknown> = {};
  for (const [field, change] of Object.entries(pending.changes ?? {})) {
    aiFields[field] = change.to;
  }
  const flipped = resolveBlocks(
    blocks,
    pending,
    "accepted",
    ctx.userId,
    ctx.resolvedAt,
  );
  const aiMetaBlob = extractMetadataBlob(aiFields, FLOW_METADATA_BLOB_FIELD);
  const flowMetadata = assembleResolvedMetadata(
    flow.flow_metadata,
    [aiMetaBlob],
    flipped,
  );
  return [
    {
      op: "update",
      id: flow.id,
      data: { ...aiFields, flowMetadata } as never,
    },
  ];
}

/**
 * Build an update op that only writes the flipped `ai_suggestions[]`
 * array — no column writes. Used by the decline path for
 * `type:delete` / `type:modify` blocks where the row content stays
 * unchanged.
 */
function flipFlowBlockOnly(
  ctx: AiResolveContext,
  flow: PreOrderFlow,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  status: Exclude<AiSuggestionBlock["status"], "pending">,
): PreOrderFlowChange {
  const flipped = resolveBlocks(
    blocks,
    pending,
    status,
    ctx.userId,
    ctx.resolvedAt,
  );
  const flowMetadata = assembleResolvedMetadata(
    flow.flow_metadata,
    [],
    flipped,
  );
  return { op: "update", id: flow.id, data: { flowMetadata } };
}

// ---------- field bucket helpers ----------

/**
 * For a `type:add` accept with user edits: collect fields the user
 * wrote with a value materially different from the row's current
 * column value. Excludes the system-managed `status` and
 * `flowMetadata` keys (the resolver writes those itself).
 */
function collectAddOverrides(
  flow: PreOrderFlow,
  userData: FlowData,
): Record<string, unknown> {
  const overrides: Record<string, unknown> = {};
  for (const f of Object.keys(userData)) {
    if (f === "flowMetadata" || f === "status") continue;
    const userVal = userData[f];
    const snakeKey = flowCamelKeyToSnake(f);
    const currentVal = snakeKey
      ? (flow as unknown as Record<string, unknown>)[snakeKey]
      : undefined;
    if (jsonEqual(userVal, currentVal ?? null)) continue;
    overrides[f] = userVal;
  }
  return overrides;
}

/**
 * For a `type:modify` accept: split AI's `block.changes` keys into
 * fields the user wrote a different value for (`overrides`) vs fields
 * the user did NOT touch — or echoed AI's value (`aiOnly`).
 */
function splitModifyIntoBuckets(
  blockChanges: Record<string, { to: unknown }>,
  userData: FlowData,
): {
  overrides: Record<string, unknown>;
  aiOnly: Record<string, unknown>;
} {
  const overrides: Record<string, unknown> = {};
  const aiOnly: Record<string, unknown> = {};
  for (const [field, change] of Object.entries(blockChanges)) {
    if (!(field in userData)) {
      aiOnly[field] = change.to;
      continue;
    }
    const userVal = userData[field];
    if (jsonEqual(userVal, change.to)) {
      aiOnly[field] = change.to;
    } else {
      overrides[field] = userVal;
    }
  }
  return { overrides, aiOnly };
}

/**
 * User-shipped flow keys that don't overlap with the AI block's
 * `changes` (and aren't the metadata blob sentinel). They get spread
 * onto the final column writes so an unrelated edit on the same row
 * survives the merge.
 */
function pickIndependentFlowFields(
  userData: FlowData,
  blockChanges: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of Object.keys(userData)) {
    if (f === "flowMetadata") continue;
    if (f in blockChanges) continue;
    out[f] = userData[f];
  }
  return out;
}
